import { useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNurseryId } from "@/hooks/useNurseryId";
import { createUniqueRealtimeTopic, isRealtimeCallbackOrderError, SAFE_REALTIME_CHANNEL_OPTIONS } from "@/lib/realtimeChannels";

export interface BreakCoverRequest {
  id: string;
  nursery_id: string;
  shift_id: string | null;
  staff_id: string | null;
  staff_name: string;
  zone_id: string | null;
  zone_name: string;
  status: "pending" | "cover_arranged" | "escalated" | "cancelled";
  required_adults: number;
  signed_in_children: number;
  simulated_counted: number;
  legal_ratio: string;
  current_ratio: string;
  requested_by: string | null;
  resolved_by: string | null;
  resolved_by_name: string;
  resolved_at: string | null;
  escalated_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Live list of pending/escalated break cover requests for the current nursery. */
export function useActiveBreakCoverRequests() {
  const qc = useQueryClient();
  const nurseryId = useNurseryId();

  const query = useQuery({
    queryKey: ["break_cover_requests", nurseryId],
    enabled: !!nurseryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("break_cover_requests" as any)
        .select("*")
        .eq("nursery_id", nurseryId!)
        .in("status", ["pending", "escalated"])
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as BreakCoverRequest[];
    },
  });

  useEffect(() => {
    if (!nurseryId) return;
    let ch: ReturnType<typeof supabase.channel> | null = null;
    try {
      ch = supabase
        .channel(createUniqueRealtimeTopic("break-cover", nurseryId), SAFE_REALTIME_CHANNEL_OPTIONS)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "break_cover_requests", filter: `nursery_id=eq.${nurseryId}` },
          () => qc.invalidateQueries({ queryKey: ["break_cover_requests", nurseryId] }),
        )
        .subscribe((status, err) => {
          if (status === "CHANNEL_ERROR") {
            console.warn("[break-cover-realtime] Subscription error; manager alerts will refresh on query invalidation.", err);
          }
        });
    } catch (error) {
      const message = isRealtimeCallbackOrderError(error)
        ? "Realtime channel collision prevented; manager alerts will refresh on query invalidation."
        : "Realtime setup failed; manager alerts will refresh on query invalidation.";
      console.warn(`[break-cover-realtime] ${message}`, error);
      if (ch) supabase.removeChannel(ch);
      ch = null;
    }
    return () => {
      if (ch) supabase.removeChannel(ch);
    };
  }, [nurseryId, qc]);

  return query;
}

/** Manager confirms cover has been arranged → flips the requester to on_break and resolves the request. */
export function useConfirmBreakCover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: BreakCoverRequest) => {
      const { data: { user } } = await supabase.auth.getUser();
      let resolverName = "Manager";
      if (user) {
        const { data: p } = await supabase
          .from("profiles")
          .select("first_name,last_name")
          .eq("user_id", user.id)
          .maybeSingle();
        if (p) resolverName = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || resolverName;
      }
      // Flip the shift to on_break
      if (req.shift_id) {
        await supabase
          .from("shifts")
          .update({ duty_status: "on_break", duty_status_changed_at: new Date().toISOString() } as any)
          .eq("id", req.shift_id);
      }
      const { error } = await supabase
        .from("break_cover_requests" as any)
        .update({
          status: "cover_arranged",
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id ?? null,
          resolved_by_name: resolverName,
          updated_at: new Date().toISOString(),
        })
        .eq("id", req.id);
      if (error) throw error;
    },
    onSuccess: (_d, req) => {
      qc.invalidateQueries({ queryKey: ["break_cover_requests"] });
      qc.invalidateQueries({ queryKey: ["shifts"] });
      // Notify requesting staff member their break has started
      if (req.requested_by) {
        supabase.from("notifications").insert({
          user_id: req.requested_by,
          message: `Break cover confirmed for ${req.zone_name}. You are now On break.`,
          severity: "info",
        });
      }
    },
  });
}

export function useCancelBreakCover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: BreakCoverRequest) => {
      const { error } = await supabase
        .from("break_cover_requests" as any)
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", req.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["break_cover_requests"] }),
  });
}
